import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  ChevronDown,
  Home,
  RefreshCcw,
  XCircle,
} from "lucide-react";
import PropTypes from "prop-types";
import { useState } from "react";

// Consistent animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 },
  },
};

const iconAnimation = {
  initial: { rotate: -90, opacity: 0 },
  animate: { rotate: 0, opacity: 1 },
  exit: { rotate: 90, opacity: 0 },
  transition: { duration: 0.3 },
};

// Custom spinner component with theme-consistent styling
const LoadingSpinner = () => (
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
    className="w-5 h-5"
  >
    <svg viewBox="0 0 24 24" role="status" aria-label="Loading">
      <defs>
        <linearGradient id="spinner-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
      </defs>
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="url(#spinner-gradient)"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
        strokeDasharray="40"
        strokeDashoffset="10"
      />
    </svg>
  </motion.div>
);

// Custom motion button component for consistency
const MotionButton = ({ children, ...props }) => (
  <motion.div
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    transition={{ type: "spring", stiffness: 400, damping: 17 }}
  >
    <Button {...props}>{children}</Button>
  </motion.div>
);

export function ErrorFallback({ error, resetErrorBoundary }) {
  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const [loadingHome, setLoadingHome] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleRefresh = async () => {
    try {
      setLoadingRefresh(true);
      // Add a small delay for better user experience
      await new Promise((resolve) => setTimeout(resolve, 600));
      window.location.reload();
      resetErrorBoundary();
    } catch (error) {
      console.error("Error during refresh:", error);
    } finally {
      setLoadingRefresh(false);
    }
  };

  const handleNavigateHome = async () => {
    try {
      setLoadingHome(true);
      // Add a small delay for better user experience
      await new Promise((resolve) => setTimeout(resolve, 600));
      window.location.href = "/";
    } catch (error) {
      console.error("Error during navigation:", error);
    } finally {
      setLoadingHome(false);
    }
  };

  // Helper for error message formatting
  const formatErrorMessage = () => {
    if (!error) return "Unknown error";

    const message = error.message || "Unknown error";
    const stack = error.stack
      ? error.stack.split("\n").slice(1).join("\n")
      : "";

    return { message, stack };
  };

  const { message, stack } = formatErrorMessage();

  return (
    <motion.div
      className="min-h-screen flex items-center justify-center bg-background p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="w-full max-w-md"
        variants={fadeIn}
        initial="hidden"
        animate="visible"
      >
        {/* Gradient border with subtle pulse animation */}
        <motion.div
          className="bg-gradient-to-r from-purple-400 via-purple-500 to-purple-600 p-[2px] rounded-xl shadow-lg overflow-hidden"
          animate={{
            boxShadow: [
              "0 0 10px rgba(139, 92, 246, 0.3)",
              "0 0 20px rgba(139, 92, 246, 0.5)",
              "0 0 10px rgba(139, 92, 246, 0.3)",
            ],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Card className="bg-card rounded-lg border-0">
            <CardHeader className="pt-6 pb-0">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="flex justify-center"
              >
                <XCircle className="h-16 w-16 text-purple-500 mb-2" />
              </motion.div>
            </CardHeader>
            <CardContent className="p-6">
              <Alert
                variant="destructive"
                className="mb-6 border-purple-200 bg-purple-50 dark:bg-opacity-5"
              >
                <AlertTriangle className="h-5 w-5 text-purple-500" />
                <AlertTitle className="ml-2 text-purple-700 dark:text-purple-300">
                  Something went wrong
                </AlertTitle>
                <AlertDescription className="mt-2 text-muted-foreground">
                  We encountered an unexpected error. Our team has been notified
                  and is working to resolve the issue.
                </AlertDescription>
              </Alert>

              <motion.div
                className="mt-6 space-y-4"
                variants={fadeIn}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.2 }}
              >
                <motion.div
                  className="p-4 bg-muted/50 backdrop-blur-sm rounded-lg border border-purple-100 dark:border-purple-900/30 cursor-pointer"
                  onClick={() => setShowError(!showError)}
                  whileHover={{ y: -2 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300">
                      View Error Details
                    </h3>
                    <motion.div
                      animate={{ rotate: showError ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ChevronDown className="h-4 w-4 text-purple-500" />
                    </motion.div>
                  </div>

                  <AnimatePresence>
                    {showError && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <pre className="mt-3 p-3 bg-background/90 rounded-md text-xs overflow-auto max-h-40 border border-purple-100 dark:border-purple-900/30">
                          <code className="text-purple-700 dark:text-purple-300 font-mono">
                            {message}
                          </code>
                          {stack && (
                            <code className="block mt-2 text-muted-foreground font-mono text-xs opacity-70">
                              {stack}
                            </code>
                          )}
                        </pre>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>

                <motion.div
                  className="flex flex-col sm:flex-row gap-3 mt-6 justify-center w-full"
                  variants={fadeIn}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.4 }}
                >
                  <MotionButton
                    variant="outline"
                    className="flex-1 border-purple-200 hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                    onClick={handleRefresh}
                    disabled={loadingRefresh}
                  >
                    {loadingRefresh ? (
                      <div className="flex items-center justify-center">
                        <LoadingSpinner />
                        <span className="ml-2">Refreshing...</span>
                      </div>
                    ) : (
                      <motion.div
                        className="flex items-center justify-center text-purple-500"
                        {...iconAnimation}
                      >
                        <RefreshCcw className="mr-2 h-4 w-4 text-purple-500" />
                        Refresh Page
                      </motion.div>
                    )}
                  </MotionButton>

                  <MotionButton
                    variant="default"
                    // make these broder colros grey not black, not sinz but instread use from this list of shadcn colors:
                    // - zinc
                    // - neutal
                    // - slate
                    // - gray
                    // - stone
                    // - red
                    // - orange
                    // - amber
                    // - yellow
                    // - green
                    // - emerald
                    // - teal
                    // - cyan
                    // - sky
                    // - blue
                    className="flex-1 border-2 border-slate-200 hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900/20 hover:text-white"
                    onClick={handleNavigateHome}
                    disabled={loadingHome}
                  >
                    {loadingHome ? (
                      <div className="flex items-center justify-center">
                        <LoadingSpinner />
                        <span className="ml-2">Navigating...</span>
                      </div>
                    ) : (
                      <motion.div
                        className="flex items-center justify-center"
                        {...iconAnimation}
                      >
                        <Home className="mr-2 h-4 w-4" />
                        Go to Homepage
                      </motion.div>
                    )}
                  </MotionButton>
                </motion.div>
              </motion.div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

ErrorFallback.displayName = "ErrorFallback";

// PropTypes
ErrorFallback.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
    stack: PropTypes.string,
  }),
  resetErrorBoundary: PropTypes.func.isRequired,
};

// Named and default exports
export default ErrorFallback;
